import { REPORTS_QA } from 'constants/routePaths';

import * as React from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import CreateProjectReport from '@graphql/mutations/CreateProjectReport';
import QaPreviewProjectsQuery, {
  QaPreviewProjectsResponse,
} from '@graphql/queries/QaPreviewProjectsQuery';
import QaPreviewReportQuery, {
  QaPreviewReportResponse,
} from '@graphql/queries/QaPreviewReportQuery';
import { Alert as MuiAlert, CircularProgress, Paper } from '@mui/material';
import ProjectSummary from 'containers/ProjectSummary';
import useUpdateReportQa from 'hooks/useUpdateReportQa';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import Alert from 'react-s-alert';
import {
  clearQaPreviewSession,
  setQaPreviewSession,
} from 'utils/qaPreviewSession';
import { isSuccessful } from 'utils/reportUtils';

import {
  parsePreviewUrlState,
  QaPreviewUrlState,
  resolvePreviewProjectId,
  serializePreviewUrlState,
} from './previewContext';
import QaPreviewToolbar, { QaDecision } from './QaPreviewToolbar';
import styles from './ReportQAPreview.module.css';

type Props = RouteComponentProps<
  { reportId: string },
  never,
  { from?: string } | undefined
>;

const ReportQAPreview: React.FC<Props> = ({ match, history, location }) => {
  const { t } = useTranslation();
  const { reportId } = match.params;
  const returnTo = location.state?.from || REPORTS_QA;
  const urlState = useMemo(
    () => parsePreviewUrlState(location.search),
    [location.search],
  );

  const {
    data: reportData,
    loading: reportLoading,
    refetch: refetchReport,
  } = useQuery<QaPreviewReportResponse>(QaPreviewReportQuery, {
    variables: { reportId },
    fetchPolicy: 'cache-and-network',
  });
  const report = reportData?.reports.nodes[0];

  const {
    data: projectsData,
    loading: projectsLoading,
    refetch: refetchProjects,
  } = useQuery<QaPreviewProjectsResponse>(QaPreviewProjectsQuery, {
    variables: { workspaceId: report?.workspaceId, reportId },
    skip: !report?.workspaceId,
    fetchPolicy: 'cache-and-network',
  });
  const projects = projectsData?.projects.nodes ?? [];
  const projectsReady = !!projectsData || (!projectsLoading && !!report);

  const projectId = resolvePreviewProjectId(
    urlState.project,
    projects,
    report?.projectName,
  );
  const mapOnly = urlState.mapOnly ?? !!report?.mapOnly;

  const { updateReportQa, updating } = useUpdateReportQa();
  const [addToProject, { loading: adding }] = useMutation(CreateProjectReport);

  const updateUrl = useCallback(
    (patch: Partial<QaPreviewUrlState>) => {
      history.replace({
        pathname: location.pathname,
        search: serializePreviewUrlState({ ...urlState, ...patch }),
        state: location.state,
      });
    },
    [history, location.pathname, location.state, urlState],
  );

  // Lets drill-down pages opened from the preview render this unapproved
  // report and send the browser back here.
  useEffect(() => {
    setQaPreviewSession({
      reportId,
      projectId: projectId ?? null,
      returnPath: `${location.pathname}${location.search}`,
    });
  }, [reportId, projectId, location.pathname, location.search]);

  const goBack = useCallback(() => {
    clearQaPreviewSession();
    history.push(returnTo);
  }, [history, returnTo]);

  const handleDecision = useCallback(
    async (decision: QaDecision) => {
      if (!report) return;
      switch (decision) {
        case 'approve':
        case 'approveAndAdd': {
          // Approve exactly what the reviewer previewed (full or map only)
          const approved = await updateReportQa(report.id, true, mapOnly);
          if (!approved) return;
          if (decision === 'approveAndAdd' && projectId) {
            const alreadyAdded = projects.some(
              (p) => p.id === projectId && p.containsReport.totalCount > 0,
            );
            if (!alreadyAdded) {
              try {
                await addToProject({
                  variables: { input: { projectId, reportId } },
                });
              } catch (error) {
                console.error(error);
                Alert.error(t('reportsQa.preview.addToProjectFailed'), {
                  position: 'top-right',
                  effect: 'slide',
                  timeout: 6000,
                });
              }
            }
          }
          goBack();
          return;
        }
        case 'reject':
          if (await updateReportQa(report.id, false, false)) goBack();
          return;
        case 'reopen':
          if (await updateReportQa(report.id, null, false)) {
            await Promise.all([refetchReport(), refetchProjects()]);
          }
          return;
        default:
      }
    },
    [
      report,
      mapOnly,
      projectId,
      projects,
      reportId,
      updateReportQa,
      addToProject,
      refetchReport,
      refetchProjects,
      goBack,
      t,
    ],
  );

  if (!report) {
    return (
      <Paper className={styles.message}>
        {reportLoading ? (
          <CircularProgress color="secondary" />
        ) : (
          <MuiAlert severity="error">{t('reportsQa.preview.notFound')}</MuiAlert>
        )}
      </Paper>
    );
  }

  return (
    <div className={styles.page}>
      <QaPreviewToolbar
        report={report}
        projects={projects}
        projectsLoading={!projectsReady}
        projectId={projectId}
        mapOnly={mapOnly}
        busy={updating || adding}
        onBack={goBack}
        onProjectChange={(id) => updateUrl({ project: id, tab: undefined })}
        onMapOnlyChange={(value) =>
          updateUrl({ mapOnly: value, tab: undefined })
        }
        onDecision={handleDecision}
      />
      {!isSuccessful(report) ? (
        <Paper className={styles.message}>
          <MuiAlert severity="warning">
            {t('reportsQa.preview.notSuccessful')}
          </MuiAlert>
        </Paper>
      ) : (
        projectsReady && (
          <ProjectSummary
            // Remount when switching context so the summary reloads cleanly
            key={`${projectId ?? 'standalone'}|${mapOnly}`}
            match={match}
            history={history}
            location={location}
            qaPreview={{
              report,
              projectId,
              workspaceId: report.workspaceId,
              mapOnly,
              initialTab: urlState.tab,
              onTabChange: (tab) => updateUrl({ tab }),
            }}
          />
        )
      )}
    </div>
  );
};

export default ReportQAPreview;
